import { Alert, Box, Chip, Container, Stack, TextField } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useApiEp from "../../hooks/useApiEp";
import BackBtn from "../../widgets/BackBtn";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse } from "../../utils/util";
import { debounce } from "lodash";
import SearchIcon from "@mui/icons-material/Search";
import TitleActionsContainer from "../../widgets/TitleActionsContainer";
import PageTitle from "../../widgets/PageTitle";
import DataTable from "../../widgets/DataTable";
import QueueStatsLive from "./QueueStatsLive";
import CustomCircularP from "../../widgets/CustomCircularP";
import RenderStatusMessage from "../../widgets/RenderStatusMessage";
import StatusStatsLive from "./StatusStatsLive";
import RdCampaignCheck from "./RdCampaignCheck";
import ScheduledInfo from "./ScheduledInfo";

const RdCampaignDetail = () => {
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(30);
  const [searchParams] = useSearchParams();
  const rcsType = searchParams.get("type");
  const navigate = useNavigate();
  const [sq, setSq] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const { data, isLoading, isError, error } = useApiEp(
    `/campaign/get-campaign-report/${id}?page=${page}&limit=${limit}&type=${
      rcsType ? rcsType : ""
    }`
  );
  const {
    data: campaignData,
    isLoading: isCampaignLoading,
    isError: isCampaignError,
    error: campaignError,
  } = useApiEp(`/campaign/campaign/${id}`);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchQuery(value);
        setPage(1); // Reset to first page when searching
      }, 500),
    []
  );

  const handleSearchChange = (e) => {
    setSq(e.target.value);
    debouncedSearch(e.target.value);
  };
  const handleAction = useCallback((action, id) => {
    switch (action) {
      case "view":
        navigate(`/home/${id}/message-detail?isRcs=${rcsType === "rcs"}`);
        break;
      default:
        console.warn("unknown action", id);
    }
  }, []);

  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }
  return (
    <Container sx={{ mt: 3 }}>
      <BackBtn />
      <CustomCircularP show={isLoading} />
      <TitleActionsContainer>
        <PageTitle>Campaign Report</PageTitle>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{
            width: { xs: "100%", sm: "auto" },
            mt: { xs: 1, sm: 0 },
          }}
        ></Stack>
      </TitleActionsContainer>
      {campaignData &&
        campaignData.status === "scheduled" &&
        campaignData.category === "scheduled" && (
          <Box>
            <ScheduledInfo data={campaignData} />
          </Box>
        )}
      <QueueStatsLive id={id} />
      <StatusStatsLive id={id} type={rcsType} />

      <DataTable
        sx={{ mb: 1 }}
        columns={[
          {
            id: "serial",
            label: "S.No.",
            render: (_v, _row, idx) => (page - 1) * limit + idx + 1,
          },
          { id: "campaignName", label: "Campaign" },
          { id: "to", label: "Recipient" },
          {
            id: "status",
            label: "Status",
            render: (v) => <RenderStatusMessage status={v} />,
          },
          {
            id: "createdOn",
            label: "Created On",
            render: (v) =>
              new Date(v).toLocaleString("en-IN", {
                dateStyle: "medium",
              }),
          },
        ]}
        actions={["view"]}
        rows={data?.data ?? []}
        page={page}
        limit={limit}
        totalPages={data?.totalPages ?? 1}
        onPageChange={setPage}
        onAction={handleAction}
      />
    </Container>
  );
};

export default RdCampaignDetail;
